import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/** Generic per-user UI preferences blob (home layout, etc.) — follows the user across devices. */
export type UiPrefs = Record<string, unknown>;

export function useUiPrefs() {
  return useQuery({
    queryKey: ['ui-prefs'],
    queryFn: () => api<{ data: UiPrefs }>('/me/ui-prefs').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useSaveUiPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: UiPrefs) =>
      api<{ data: UiPrefs }>('/me/ui-prefs', { method: 'PUT', body: prefs }).then((r) => r.data),
    // Optimistically keep the cache in sync so reads reflect the save immediately.
    onMutate: (prefs) => {
      qc.setQueryData(['ui-prefs'], prefs);
    },
  });
}

/**
 * A single preference synced across devices via `ui_prefs` (server = source of
 * truth) with a localStorage cache for instant paint. Works like useState.
 *
 * Example: const [railExpanded, setRailExpanded] = usePref('railExpanded', false)
 */
export function usePref<T>(key: string, fallback: T): [T, (v: T | ((prev: T) => T)) => void] {
  const prefs = useUiPrefs();
  const save = useSaveUiPrefs();
  const lsKey = `iheart:pref:${key}`;

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw !== null) return JSON.parse(raw) as T;
    } catch {
      /* ignore */
    }
    return fallback;
  });

  // Adopt the server value once it loads (if it has one).
  useEffect(() => {
    if (prefs.data && key in prefs.data) setValue(prefs.data[key] as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.data]);

  const set = useCallback(
    (v: T | ((prev: T) => T)) => {
      const next = v instanceof Function ? (v as (p: T) => T)(value) : v;
      setValue(next);
      try {
        localStorage.setItem(lsKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      const base = (prefs.data ?? {}) as UiPrefs;
      save.mutate({ ...base, [key]: next });
    },
    [value, prefs.data, save, key, lsKey],
  );

  return [value, set];
}
